//----------------------------------------------------------------------------------------------------------------------
// Game Entity
//----------------------------------------------------------------------------------------------------------------------

import type { TransformNode } from '@babylonjs/core';

import type { GameEntityBehaviorConstructor } from '../interfaces/entity.ts';
import type { Destroyable } from '../interfaces/lifecycle.ts';
import type { ColliderDebugConfig } from '../managers/colliderDebug.ts';
import { type GameEvent, GameEventBus } from './eventBus.ts';
import type { GameEngine } from './gameEngine.ts';
import { generateId } from '../utils/id.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Utility type that removes readonly from all properties.
 * Used internally to allow controlled mutation of readonly properties.
 */
type Mutable<T> = { -readonly [P in keyof T] : T[P] };

/**
 * Result of a request sent to an entity's behaviors.
 * `success: true` means a behavior handled the request and returned a value.
 * `success: false` means no behavior handled it, or a behavior threw an error.
 */
export type RequestResult<T> = { success : true; value : T } | { success : false; error : string };

/**
 * Narrow interface for entity-to-manager messaging delegation.
 * Avoids a circular import between entity.ts and managers/entity.ts.
 */
interface EntityMessenger
{
    send(target : string, type : string, payload ?: unknown, senderID ?: string) : Promise<void>;
    request<T>(target : string, type : string, payload ?: unknown, senderID ?: string) : Promise<RequestResult<T>>;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Tracks a reference-counted event subscription so multiple behaviors sharing the same event type
 * only produce a single underlying subscription.
 */
interface GameEventSubscription
{
    count : number;
    unsubscribe : () => void;
}

/**
 * Options for attaching a behavior at a specific position.
 * Only one positioning option should be specified.
 */
export interface AttachBehaviorOptions
{
    /** Insert before behavior of this class */
    before ?: GameEntityBehaviorConstructor;
    /** Insert after behavior of this class */
    after ?: GameEntityBehaviorConstructor;
    /** Insert at specific index (0-based, clamped to valid range) */
    at ?: number;
}

/**
 * Minimal entity surface exposed to behaviors, preventing them from accessing internals like
 * subscription management or behavior ordering.
 */
interface SimpleGameEntity<EntityState extends object = object>
{
    id : string;
    type : string;
    name ?: string;
    node ?: TransformNode;
    state : EntityState;
    eventBus : GameEventBus;
    tags : ReadonlySet<string>;

    send(target : string, type : string, payload ?: unknown) : Promise<void>;
    request<T>(target : string, type : string, payload ?: unknown) : Promise<RequestResult<T>>;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Base class for game entity behaviors. Subclass this to define how an entity reacts to events
 * and updates each frame. Multiple behaviors can be attached to a single entity; event processing
 * order matches the attachment order.
 */
export abstract class GameEntityBehavior<RequiredState extends object = object>
{
    abstract name : string;
    abstract eventSubscriptions : string[];

    protected entity : SimpleGameEntity<RequiredState> | null = null;

    //------------------------------------------------------------------------------------------------------------------
    // Internal API
    //------------------------------------------------------------------------------------------------------------------

    $emit(event : GameEvent) : void
    {
        if(!this.entity)
        {
            throw new Error('Entity is not set for this behavior.');
        }

        event.senderID = this.entity.id;
        // Cast to allow publishing events with arbitrary payloads
        (this.entity.eventBus as unknown as GameEventBus<Record<string, unknown>>).publish(event);
    }

    /**
     * Emits an entity:state-changed event to notify UI or other observers of state changes.
     * Call this after making significant state changes that external systems should know about.
     *
     * @param state - Optional state to include in the event. If omitted, uses current entity state.
     * @param changes - Optional record of specific changes made (alternative to full state).
     */
    $emitStateChanged(state ?: object, changes ?: Record<string, unknown>) : void
    {
        if(!this.entity)
        {
            throw new Error('Entity is not set for this behavior.');
        }

        this.$emit({
            type: 'entity:state-changed',
            payload: {
                entityId: this.entity.id,
                entityType: this.entity.type,
                state: state ?? this.entity.state,
                changes,
            },
        });
    }

    /**
     * Sets the entity for this behavior.
     * @param entity
     */
    $setEntity(entity : SimpleGameEntity<RequiredState> | null) : void
    {
        this.entity = entity;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Implementation API
    //------------------------------------------------------------------------------------------------------------------

    abstract processEvent(event : GameEvent, state : RequiredState) : Promise<boolean> | boolean;

    /**
     * Handles a direct request sent to this entity. Return a value to respond,
     * or `undefined` to pass the request to the next behavior.
     *
     * @param event - The request event (type, payload, senderID, targetID)
     * @param state - Current entity state
     */
    processRequest?(event : GameEvent, state : RequiredState) : Promise<unknown> | unknown | undefined;

    update?(dt : number, state : RequiredState) : void;

    destroy?() : Promise<void>;

    //------------------------------------------------------------------------------------------------------------------
    // Lifecycle Hooks
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Called when the entity is attached to a scene node.
     * Override to perform initialization that requires the node (physics, spatial audio, etc.)
     *
     * The scene is accessible via `node.getScene()`.
     *
     * @param node - The TransformNode (typically a Mesh) the entity is attached to.
     * @param gameEngine - The game engine instance for accessing managers and engines.
     */
    onNodeAttached?(node : TransformNode, gameEngine : GameEngine) : void;

    /**
     * Called when the entity is detached from its scene node.
     * Override to clean up node-dependent resources.
     */
    onNodeDetached?() : void;

    /**
     * Called when the entity is recycled from an object pool.
     * Override to reset behavior-specific state that isn't covered by entity state reset.
     * Distinct from onNodeDetached — this signals a full lifecycle reset.
     *
     * @param state - The freshly reset entity state.
     */
    onReset?(state : RequiredState) : void;
}

//----------------------------------------------------------------------------------------------------------------------

/**
 * Base class for game entities. Owns a set of behaviors, manages event subscriptions
 * (reference-counted per event type), and delegates lifecycle calls to its behaviors.
 */
export class GameEntity<EntityState extends object = object> implements SimpleGameEntity<EntityState>, Destroyable
{
    /** The unique identifier of the entity (16-char hex string). */
    public readonly id : string;

    public readonly type : string;

    /** Optional human-readable name for the entity. */
    public readonly name ?: string;

    /**
     * The scene node this entity is attached to (if any).
     * When attached, the entity controls this node's behavior.
     * Use EntityManager.attachToNode() / detachFromNode() to modify.
     */
    public readonly node ?: TransformNode;

    /** Child entities in the hierarchy. */
    public readonly children : GameEntity[] = [];

    /** Parent entity in the hierarchy, or null if this is a root entity. */
    public readonly parent : GameEntity | null = null;

    public state : EntityState;

    /** Array of behaviors attached to the entity. Order determines event processing priority. */
    public behaviors : GameEntityBehavior[] = [];

    public eventBus : GameEventBus;

    private _gameEngine : GameEngine | null = null;
    private _entityManager : EntityMessenger | null = null;
    private _tags : Set<string> = new Set<string>();
    private subscriptions : Map<string, GameEventSubscription> = new Map<string, GameEventSubscription>();

    /**
     * Creates an instance of GameEntity.
     * @param type
     * @param eventBus
     * @param initialState
     * @param behaviors - Attached in order; order determines event processing priority.
     * @param name
     */
    constructor(
        type : string,
        eventBus : GameEventBus,
        initialState : EntityState,
        behaviors : GameEntityBehaviorConstructor[],
        name ?: string
    )
    {
        this.id = generateId();
        this.type = type;
        this.name = name;
        this.state = initialState;
        this.eventBus = eventBus;

        // Attach behaviors
        for(const behaviorCtor of behaviors)
        {
            this.attachBehavior(new behaviorCtor());
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Tags
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Read-only access to the entity's tags.
     */
    get tags() : ReadonlySet<string>
    {
        return this._tags;
    }

    /**
     * Checks if the entity has a specific tag.
     * @param tag
     */
    hasTag(tag : string) : boolean
    {
        return this._tags.has(tag);
    }

    /**
     * Internal method for EntityManager to add a tag.
     * @param tag
     */
    $addTag(tag : string) : void
    {
        this._tags.add(tag);
    }

    /**
     * Internal method for EntityManager to remove a tag.
     * @param tag
     */
    $removeTag(tag : string) : boolean
    {
        return this._tags.delete(tag);
    }

    //------------------------------------------------------------------------------------------------------------------
    // Node Attachment
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Find a direct child node by name.
     * @param name
     */
    getChildNode(name : string) : TransformNode | undefined
    {
        if(!this.node)
        {
            return undefined;
        }

        return this.node.getChildren(
            (child) => child.name === name,
            false
        )[0] as TransformNode | undefined;
    }

    /**
     * Find a descendant node by path (e.g., "armature/hand/finger").
     * @param path - Slash-separated path of node names.
     */
    findNode(path : string) : TransformNode | undefined
    {
        if(!this.node)
        {
            return undefined;
        }

        let current : TransformNode | undefined = this.node;

        for(const part of path.split('/'))
        {
            current = current?.getChildren(
                (child) => child.name === part,
                false
            )[0] as TransformNode | undefined;
        }

        return current;
    }

    /**
     * Internal method for EntityManager to attach entity to a node.
     * Do not call directly - use EntityManager.attachToNode() instead.
     * @param node
     * @param gameEngine
     * @internal
     */
    $attachToNode(node : TransformNode, gameEngine : GameEngine) : void
    {
        (this as Mutable<GameEntity>).node = node;
        this._gameEngine = gameEngine;

        // Notify behaviors of node attachment
        for(const behavior of this.behaviors)
        {
            behavior.onNodeAttached?.(node, gameEngine);
        }

        // Parent any children's nodes under this node
        for(const child of this.children)
        {
            if(child.node)
            {
                (child.node as { parent : unknown }).parent = node;
            }
        }
    }

    /**
     * Internal method for EntityManager to detach entity from its node.
     * Do not call directly - use EntityManager.detachFromNode() instead.
     * @internal
     */
    $detachFromNode() : void
    {
        // Remove from all outline layers before detaching
        this.unhighlight();

        // Detach children's nodes before detaching self
        for(const child of this.children)
        {
            if(child.node)
            {
                (child.node as { parent : unknown }).parent = null;
            }
        }

        // Notify behaviors before detaching
        for(const behavior of this.behaviors)
        {
            behavior.onNodeDetached?.();
        }

        (this as Mutable<GameEntity>).node = undefined;
        this._gameEngine = null;
    }

    /**
     * Add this entity to a named outline layer for visual highlighting.
     * Requires the entity to be attached to a node in a level with outlines configured.
     *
     * @param layerName - The name of the outline layer (must exist on the current level)
     */
    highlight(layerName : string) : void
    {
        const outlines = this._gameEngine?.managers?.levelManager?.currentLevel?.outlines;
        if(outlines)
        {
            outlines.highlightEntity(layerName, this);
        }
    }

    /**
     * Remove this entity from an outline layer, or from all layers if no name given.
     *
     * @param layerName - Specific layer to remove from, or omit to remove from all
     */
    unhighlight(layerName ?: string) : void
    {
        const outlines = this._gameEngine?.managers?.levelManager?.currentLevel?.outlines;
        if(outlines)
        {
            if(layerName)
            {
                outlines.unhighlightEntity(layerName, this);
            }
            else
            {
                outlines.unhighlightEntityAll(this);
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Collider Debug
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Shows collider debug visualization for this entity's physics nodes.
     *
     * Overloads:
     * - `showCollider()` — show all colliders, default color
     * - `showCollider(true)` — show all colliders, default color (config-driven)
     * - `showCollider('#ff0000')` — show all colliders with a specific color
     * - `showCollider('head')` — show a specific collider by node name
     * - `showCollider('head', '#ff0000')` — show a specific collider by name with color
     * - `showCollider({ head: '#ff0000', body: false })` — per-collider control
     */
    showCollider(configOrNameOrColor ?: ColliderDebugConfig, color ?: string) : void
    {
        if(!this.node || !this._gameEngine)
        {
            return;
        }

        const debugManager = this._gameEngine.debug?.colliders;
        if(!debugManager)
        {
            return;
        }

        const nodes = this._getPhysicsNodes();

        // No args or true: show all with default color
        if(configOrNameOrColor === undefined || configOrNameOrColor === true)
        {
            for(const node of nodes)
            {
                debugManager.showColliderForNode(node, undefined);
            }
            return;
        }

        // String argument
        if(typeof configOrNameOrColor === 'string')
        {
            // Starts with '#' — it's a color for all nodes
            if(configOrNameOrColor.startsWith('#'))
            {
                for(const node of nodes)
                {
                    debugManager.showColliderForNode(node, configOrNameOrColor);
                }
                return;
            }

            // Otherwise it's a node name
            const target = nodes.find((nd) => nd.name === configOrNameOrColor);
            if(target)
            {
                debugManager.showColliderForNode(target, color);
            }
            return;
        }

        // Object: per-node config
        if(typeof configOrNameOrColor === 'object')
        {
            const config = configOrNameOrColor as Record<string, string | false>;
            for(const node of nodes)
            {
                const entry = config[node.name];
                if(entry)
                {
                    debugManager.showColliderForNode(node, entry);
                }
            }
        }
    }

    /**
     * Hides collider debug visualization for this entity's physics nodes.
     *
     * - `hideCollider()` — hide all colliders
     * - `hideCollider('head')` — hide a specific collider by node name
     */
    hideCollider(name ?: string) : void
    {
        if(!this.node || !this._gameEngine)
        {
            return;
        }

        const debugManager = this._gameEngine.debug?.colliders;
        if(!debugManager)
        {
            return;
        }

        const nodes = this._getPhysicsNodes();

        if(name === undefined)
        {
            for(const node of nodes)
            {
                debugManager.hideColliderForNode(node);
            }
            return;
        }

        const target = nodes.find((nd) => nd.name === name);
        if(target)
        {
            debugManager.hideColliderForNode(target);
        }
    }

    private _getPhysicsNodes() : TransformNode[]
    {
        if(!this.node)
        {
            return [];
        }

        const nodes : TransformNode[] = [];

        if(this.node.physicsBody)
        {
            nodes.push(this.node);
        }

        for(const child of this.node.getChildTransformNodes(false))
        {
            if(child.physicsBody)
            {
                nodes.push(child);
            }
        }

        return nodes;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Child Hierarchy
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Adds a child entity to this entity's hierarchy.
     * Sets the child's parent to this entity. If both entities have nodes, parents the child's node.
     * @param child
     */
    $addChild(child : GameEntity) : void
    {
        this.children.push(child);
        (child as Mutable<GameEntity>).parent = this;

        // If both have nodes, parent the child's node under ours
        if(this.node && child.node)
        {
            (child.node as { parent : unknown }).parent = this.node;
        }
    }

    /**
     * Removes a child entity from this entity's hierarchy.
     * Clears the child's parent reference. If the child has a node, detaches it.
     * @param child
     */
    $removeChild(child : GameEntity) : void
    {
        const index = this.children.indexOf(child);
        if(index !== -1)
        {
            this.children.splice(index, 1);
            (child as Mutable<GameEntity>).parent = null;

            // Detach child node from parent node
            if(child.node)
            {
                (child.node as { parent : unknown }).parent = null;
            }
        }
    }

    //------------------------------------------------------------------------------------------------------------------
    // Messaging
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Sets the entity manager reference for cross-entity messaging.
     * Called by the EntityManager after entity creation.
     * @param manager
     * @internal
     */
    $setEntityManager(manager : EntityMessenger) : void
    {
        this._entityManager = manager;
    }

    /**
     * Sends a fire-and-forget event directly to a specific entity's behaviors, bypassing the event bus.
     * If the target matches this entity's own ID, dispatches locally.
     *
     * @param target - Entity ID or name
     * @param type - Event type string
     * @param payload - Optional event payload
     */
    async send(target : string, type : string, payload ?: unknown) : Promise<void>
    {
        if(target === this.id)
        {
            return this.$send(type, payload, this.id);
        }

        if(!this._entityManager)
        {
            throw new Error('Entity is not registered with an EntityManager.');
        }

        return this._entityManager.send(target, type, payload, this.id);
    }

    /**
     * Sends a request to a specific entity's behaviors and returns the first response.
     * If the target matches this entity's own ID, dispatches locally.
     *
     * @param target - Entity ID or name
     * @param type - Request type string
     * @param payload - Optional request payload
     */
    async request<T>(target : string, type : string, payload ?: unknown) : Promise<RequestResult<T>>
    {
        if(target === this.id)
        {
            return this.$request<T>(type, payload, this.id);
        }

        if(!this._entityManager)
        {
            throw new Error('Entity is not registered with an EntityManager.');
        }

        return this._entityManager.request<T>(target, type, payload, this.id);
    }

    /**
     * Sends a fire-and-forget event directly to this entity's own behaviors.
     *
     * @param type - Event type string
     * @param payload - Optional event payload
     * @internal
     */
    async $send(type : string, payload ?: unknown, senderID ?: string) : Promise<void>
    {
        await this.$processEvent({ type, targetID: this.id, senderID, payload: payload as never });
    }

    /**
     * Sends a request to this entity's own behaviors and returns the first response.
     *
     * @param type - Request type string
     * @param payload - Optional request payload
     * @internal
     */
    async $request<T>(type : string, payload ?: unknown, senderID ?: string) : Promise<RequestResult<T>>
    {
        const event : GameEvent = { type, targetID: this.id, senderID, payload: payload as never };
        let lastError : string | undefined;

        for(const behavior of this.behaviors)
        {
            try
            {
                // eslint-disable-next-line no-await-in-loop
                const result = await behavior.processRequest?.(event, this.state);
                if(result !== undefined)
                {
                    return { success: true, value: result as T };
                }
            }
            catch (err : unknown)
            {
                // Record the error but keep trying subsequent behaviors
                lastError = err instanceof Error ? err.message : String(err);
            }
        }

        return {
            success: false,
            error: lastError ?? `No handler for request "${ type }" on entity "${ this.name ?? this.id }"`,
        };
    }

    //------------------------------------------------------------------------------------------------------------------
    // Internal Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Resets the entity for object pool recycling.
     * Restores state and tags to defaults, detaches from node, notifies behaviors, and resets children.
     * @param defaultState - Default state from the entity definition
     * @param defaultTags - Default tags from the entity definition
     */
    $reset(defaultState : object, defaultTags : string[]) : void
    {
        // Reset state to a copy of defaults
        this.state = { ...defaultState } as EntityState;

        // Clear all tags, then re-add defaults
        this._tags.clear();
        for(const tag of defaultTags)
        {
            this._tags.add(tag);
        }

        // Detach from node if attached
        if(this.node)
        {
            this.$detachFromNode();
        }

        // Notify behaviors
        for(const behavior of this.behaviors)
        {
            behavior.onReset?.(this.state);
        }

        // Recursively reset children
        for(const child of this.children)
        {
            child.$reset({}, []);
        }

        // Clear parent reference (the manager handles the parent side)
        (this as Mutable<GameEntity>).parent = null;
    }

    /**
     * Processes a game event by passing it to the entity's behaviors.
     * @param event
     */
    async $processEvent(event : GameEvent) : Promise<void>
    {
        for(const behavior of this.behaviors)
        {
            // eslint-disable-next-line no-await-in-loop
            const result = await behavior.processEvent(event, this.state);
            if(result)
            {
                // If the behavior returns true, stop other behaviors from processing the event.
                break;
            }
        }
    }

    /**
     * Updates the entity by calling the update method of its behaviors.
     * @param dt - Seconds elapsed since the last update.
     */
    $update(dt : number) : void
    {
        for(const behavior of this.behaviors)
        {
            behavior.update?.(dt, this.state);
        }

        // Cascade update to children
        for(const child of this.children)
        {
            child.$update(dt);
        }
    }

    /**
     * Destroys the entity by calling the destroy method of its behaviors and unsubscribing from all events.
     */
    async $destroy() : Promise<void>
    {
        // Destroy children first (iterate a copy to avoid mutation during iteration)
        for(const child of [ ...this.children ])
        {
            // eslint-disable-next-line no-await-in-loop
            await child.$destroy();
        }

        this.children.length = 0;

        for(const behavior of this.behaviors)
        {
            // eslint-disable-next-line no-await-in-loop
            await behavior.destroy?.();
        }

        // Unsubscribe from all event subscriptions
        for(const subscription of this.subscriptions.values())
        {
            subscription.unsubscribe();
        }

        this.behaviors = [];
        this.subscriptions.clear();
    }

    /**
     * Finds the index of a behavior by its constructor.
     * @param behaviorClass
     */
    private findBehaviorIndex(behaviorClass : GameEntityBehaviorConstructor) : number
    {
        return this.behaviors.findIndex((bh) => bh.constructor === behaviorClass);
    }

    /**
     * Checks if a behavior of the given class is attached.
     * @param behaviorClass
     */
    hasBehavior(behaviorClass : GameEntityBehaviorConstructor) : boolean
    {
        return this.findBehaviorIndex(behaviorClass) !== -1;
    }

    /**
     * Gets a behavior by its constructor.
     * @param behaviorClass
     */
    getBehavior<T extends GameEntityBehavior>(behaviorClass : new () => T) : T | undefined
    {
        return this.behaviors.find((bh) => bh.constructor === behaviorClass) as T | undefined;
    }

    //------------------------------------------------------------------------------------------------------------------
    // Public Methods
    //------------------------------------------------------------------------------------------------------------------

    /**
     * Attaches a behavior to the entity.
     * Without options, appends to the end (most common case).
     * With options, inserts at the specified position.
     *
     * @param behavior
     * @param options
     * @throws If the behavior is already attached.
     * @throws If 'before' or 'after' target is not found.
     * @throws If more than one positioning option is specified.
     */
    attachBehavior(behavior : GameEntityBehavior, options ?: AttachBehaviorOptions) : void
    {
        // Check if a behavior of this class is already attached
        if(this.hasBehavior(behavior.constructor as GameEntityBehaviorConstructor))
        {
            throw new Error(`Behavior ${ behavior.constructor.name } is already attached to this entity.`);
        }

        // Subscribe to the behavior's event subscriptions
        for(const event of behavior.eventSubscriptions)
        {
            // Check if the event is already subscribed
            const existingSubscription = this.subscriptions.get(event);
            if(existingSubscription)
            {
                existingSubscription.count++;
            }
            else
            {
                // Create a new subscription
                // Cast eventBus to accept any payload map for entity event handling
                const bus = this.eventBus as unknown as GameEventBus<Record<string, unknown>>;
                const unsubscribe = bus.subscribe(event, this.$processEvent.bind(this));
                this.subscriptions.set(event, { count: 1, unsubscribe });
            }
        }

        // Determine insertion position
        if(!options)
        {
            // Common case: simple append
            this.behaviors.push(behavior);
        }
        else
        {
            // Validate: only one positioning option allowed
            const positioningOptions = [ options.before, options.after, options.at ]
                .filter((opt) => opt !== undefined);

            if(positioningOptions.length > 1)
            {
                throw new Error('attachBehavior: specify only one of before, after, or at');
            }

            if(options.before)
            {
                const index = this.findBehaviorIndex(options.before);
                if(index === -1)
                {
                    throw new Error(`attachBehavior: no behavior of type ${ options.before.name } found`);
                }
                this.behaviors.splice(index, 0, behavior);
            }
            else if(options.after)
            {
                const index = this.findBehaviorIndex(options.after);
                if(index === -1)
                {
                    throw new Error(`attachBehavior: no behavior of type ${ options.after.name } found`);
                }
                this.behaviors.splice(index + 1, 0, behavior);
            }
            else if(options.at !== undefined)
            {
                // Clamp to valid range rather than throwing
                const clampedIndex = Math.max(0, Math.min(options.at, this.behaviors.length));
                this.behaviors.splice(clampedIndex, 0, behavior);
            }
            else
            {
                // No positioning option specified, append
                this.behaviors.push(behavior);
            }
        }

        behavior.$setEntity(this);

        // If entity already has a node, notify the new behavior
        if(this.node && this._gameEngine)
        {
            behavior.onNodeAttached?.(this.node, this._gameEngine);
        }
    }

    /**
     * Detaches a behavior from the entity by its constructor.
     * @param behaviorClass
     */
    detachBehavior(behaviorClass : GameEntityBehaviorConstructor) : boolean
    {
        const index = this.findBehaviorIndex(behaviorClass);
        if(index === -1)
        {
            return false;
        }

        const behavior = this.behaviors[index];

        // Notify behavior of node detachment if entity has a node
        if(this.node)
        {
            behavior.onNodeDetached?.();
        }

        // Remove the behavior's event subscriptions
        for(const event of behavior.eventSubscriptions)
        {
            const subscription = this.subscriptions.get(event);
            if(subscription)
            {
                subscription.count--;
                if(subscription.count <= 0)
                {
                    subscription.unsubscribe();
                    this.subscriptions.delete(event);
                }
            }
        }

        // Remove the behavior
        this.behaviors.splice(index, 1);
        behavior.$setEntity(null);

        return true;
    }
}

//----------------------------------------------------------------------------------------------------------------------

